import { Button, Link as FluentLink, TableCell, TableRow, TabValue, Tooltip } from '@fluentui/react-components';
import { Edit16Regular } from '@fluentui/react-icons';
import { useState } from 'react';

import { isTDocument, TDocument } from '@modules/documents';
import { downloadFile, isTFile, TFile } from '@modules/files';
import { TViewApprovalDialogState, ApprovalStatusButton } from '@modules/approvals';
import { EHandlePdfBlobMode, isFileItemVersion, isTDraft } from '../../types/items.types';
import { TPermissions } from '@modules/access/types';
import { isTInlineBlock, TInlineBlock } from '@modules/inlineBlocks';
import ItemSourceActions from '../ItemSourceActions';
import HistoryTable, { THistoryRow } from '../HistoryTable';
import config from '@config/index';
import handlePdfBlob from '../../helpers/handlePdfBlob.helper';
import getItemVersions from '../../helpers/getItemVersions.helper';
import type { TReplica } from '@modules/replicas';
import { SingleFieldDialog, TSingleFieldDialogState } from '@shared/components';
import { useCanUpdateOriginalVersion } from '../../hooks/useCanUpdateOriginalVersion.hook';
import { useUpdateVersionDialog } from '../../hooks/useUpdateVersionDialog.hook';

type TProps = {
  item: TDocument | TFile | TInlineBlock;
  permissions: TPermissions;
  setSelectedTab: (value: TabValue) => void;
  setViewApprovalDialogState?: (state?: TViewApprovalDialogState) => void;
  replica?: TReplica;
};

const HistoryTab = ({ item, permissions, setSelectedTab, setViewApprovalDialogState, replica }: TProps) => {
  const isDoc = isTDocument(item);
  const isInlineBlock = isTInlineBlock(item);
  const isBlock = isInlineBlock || (isDoc && (item as TDocument).isBlock);
  const draft = isDoc || isInlineBlock ? (item as TDocument | TInlineBlock).draft : undefined;
  const versionedLabel = isDoc || isInlineBlock ? 'Versioned' : 'Uploaded';
  const approvalsByVersion = !isInlineBlock ? item.approvals.slice().reverse() : [];
  const sourceItem = isDoc ? (item as TDocument) : isTFile(item) ? item : undefined;
  const [singleFieldDialogState, setSingleFieldDialogState] = useState<TSingleFieldDialogState>();
  const { openOriginal } = useUpdateVersionDialog();
  const canUpdateOriginalVersion = useCanUpdateOriginalVersion(item, permissions, replica);
  const onUpdateOriginalVersion =
    canUpdateOriginalVersion && isDoc ? () => openOriginal(item, setSingleFieldDialogState) : undefined;

  const rows: THistoryRow[] = [
    ...(draft ? [{ id: 'draft', note: '', date: '', authorIds: item.stats.authors }] : []),
    ...getItemVersions(item).map((itemVersion) => {
      const { version } = itemVersion;
      const date = isTDraft(version)
        ? ''
        : isFileItemVersion(itemVersion)
          ? itemVersion.version.date
          : itemVersion.version.completedDate;
      return { id: version.id, note: version.note, date, authorIds: version.authors };
    }),
  ];

  const approval = isBlock
    ? undefined
    : {
        renderCell: (row: THistoryRow) => {
          if (row.id === 'draft') return '-';
          const a = approvalsByVersion.find((entry) => entry.versionId === row.id);
          return a ? (
            <ApprovalStatusButton approval={a} setViewApprovalDialogState={setViewApprovalDialogState} size="small" />
          ) : (
            '-'
          );
        },
      };

  const importDetailsRow =
    isDoc && item.importDetails ? (
      <TableRow>
        <TableCell className={onUpdateOriginalVersion ? 'whitespace-nowrap' : undefined}>
          <FluentLink
            onClick={() => {
              item.importDetails &&
                downloadFile(
                  `${config.AZURE_STORAGE_BASE_URL}/${item._id}/${encodeURIComponent(item.importDetails.blobName)}?${permissions.token}`,
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                  item.importDetails.fileName
                );
            }}
          >
            {item.originalVersionId ? `v${item.originalVersionId}` : 'Original'}
          </FluentLink>
          {onUpdateOriginalVersion && (
            <Tooltip content="Update original version number" relationship="label">
              <Button appearance="subtle" size="small" icon={<Edit16Regular />} onClick={onUpdateOriginalVersion} />
            </Tooltip>
          )}
        </TableCell>
        <TableCell>Original file uploaded to Confideck.</TableCell>
        <TableCell>-</TableCell>
        <TableCell>-</TableCell>
        {!isBlock && <TableCell>-</TableCell>}
      </TableRow>
    ) : null;

  const originalApprovedRow = sourceItem?.lastExternallyApproved?.originalApprovedVersionId ? (
    <TableRow>
      <TableCell>
        <FluentLink
          onClick={() => {
            const approved = sourceItem.lastExternallyApproved;
            if (!approved) return;
            handlePdfBlob(
              sourceItem._id,
              approved.blobName,
              permissions.token,
              EHandlePdfBlobMode.DOWNLOAD,
              approved.fileName
            );
          }}
        >
          v{sourceItem.lastExternallyApproved.originalApprovedVersionId}
        </FluentLink>
      </TableCell>
      <TableCell>Original approved PDF uploaded to Confideck.</TableCell>
      <TableCell>-</TableCell>
      <TableCell>-</TableCell>
      {!isBlock && <TableCell>-</TableCell>}
    </TableRow>
  ) : null;

  return (
    <>
      <SingleFieldDialog state={singleFieldDialogState} setState={setSingleFieldDialogState} />
      <HistoryTable
        rows={rows}
        versionedLabel={versionedLabel}
        versionColumnWidth={onUpdateOriginalVersion ? 112 : undefined}
        onRowClick={() => setSelectedTab('home')}
        approval={approval}
        extraRowsAfter={
          <>
            {importDetailsRow}
            {originalApprovedRow}
          </>
        }
        footer={sourceItem && <ItemSourceActions item={sourceItem} permissions={permissions} />}
      />
    </>
  );
};

export default HistoryTab;
